import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function GameReviewFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState("all");
  const [sortBy] = useState("recent");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  const handleEditPost = (post) => {
    setSelectedReview(null);
    setEditingPost(post);
  };
  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    setSelectedReview(null);
  };

  const platforms = [
    { id: "all", name: "Todas" },
    { id: "pc", name: "PC" },
    { id: "ps5", name: "PS5" },
    { id: "xbox", name: "Xbox" },
    { id: "switch", name: "Switch" },
    { id: "mobile", name: "Mobile" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/game_review?page=${pageNum}&limit=12`;
      if (platform !== "all") url += `&platform=${platform}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching game reviews:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [platform, sortBy]);

  const getRatingColor = (rating) => {
    if (rating >= 8) return "#4CAF50";
    if (rating >= 6) return "#FFC107";
    if (rating >= 4) return "#FF9800";
    return "#F44336";
  };

  if (loading && posts.length === 0) {
    return <div className="gamereview_feed_loading"><div className="gamereview_loader"></div><span>Carregando reviews...</span></div>;
  }

  return (
    <div className="gamereview_feed">
      {platforms.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {platforms.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${platform === filter.id ? "active" : ""}`}
                onClick={() => setPlatform(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="gamereview_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M21.58 16.09l-1.09-7.66C20.21 6.46 18.52 5 16.53 5H7.47C5.48 5 3.79 6.46 3.51 8.43l-1.09 7.66C2.2 17.63 3.39 19 4.94 19h0c.68 0 1.32-.27 1.8-.75L9 16h6l2.25 2.25c.48.48 1.13.75 1.8.75h0c1.56 0 2.75-1.37 2.53-2.91zM11 11H9v2H8v-2H6v-1h2V8h1v2h2v1zm4-1c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2 3c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
          </svg>
          <h3>Nenhuma review encontrada</h3>
          <p>Seja o primeiro a avaliar um jogo!</p>
        </div>
      ) : (
        <div className="gamereview_grid">
          {posts.map((post) => (
            <div key={post._id} className="gamereview_card" onClick={() => setSelectedReview(post)}>
              <div className="gamereview_cover">
                <img src={post.gameReviewData?.coverImage || post.images?.[0]?.url || post.images?.[0] || "/images/default-game.png"} alt="" />
                <div className="gamereview_rating_badge" style={{ background: getRatingColor(post.gameReviewData?.rating || 0) }}>
                  {post.gameReviewData?.rating?.toFixed(1) || "N/A"}
                </div>
              </div>
              <div className="gamereview_card_content">
                <h3>{post.gameReviewData?.gameTitle || post.text}</h3>
                <div className="gamereview_platforms">
                  {post.gameReviewData?.platforms?.slice(0, 3).map((p, i) => (
                    <span key={i}>{p}</span>
                  ))}
                </div>
                <p className="gamereview_excerpt">{post.gameReviewData?.summary}</p>
                <div className="gamereview_footer">
                  <Link to={`/profile/${post.user.username}`} className="gamereview_author" onClick={(e) => e.stopPropagation()}>
                    <img src={post.user.picture} alt="" />
                    <span>{post.user.first_name}</span>
                  </Link>
                  <span className="gamereview_date">
                    <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
                  </span>
                  <div onClick={(e) => e.stopPropagation()}>
                    <FeedPostMenu
                      post={post}
                      onEdit={handleEditPost}
                      onPostDeleted={handlePostDeleted}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="gamereview_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais reviews"}
          </button>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {selectedReview && (
        <div className="gamereview_modal_overlay" onClick={() => setSelectedReview(null)}>
          <div className="gamereview_modal" onClick={(e) => e.stopPropagation()}>
            <button className="gamereview_modal_close" onClick={() => setSelectedReview(null)}>✕</button>
            <div className="gamereview_modal_header">
              <img src={selectedReview.gameReviewData?.coverImage || selectedReview.images?.[0]} alt="" />
              <div className="gamereview_modal_title">
                <h2>{selectedReview.gameReviewData?.gameTitle}</h2>
                <div className="gamereview_modal_platforms">
                  {selectedReview.gameReviewData?.platforms?.map((p, i) => (
                    <span key={i}>{p}</span>
                  ))}
                </div>
                <div className="gamereview_modal_rating" style={{ background: getRatingColor(selectedReview.gameReviewData?.rating || 0) }}>
                  <span className="gamereview_rating_number">{selectedReview.gameReviewData?.rating?.toFixed(1)}</span>
                  <span className="gamereview_rating_label">/10</span>
                </div>
              </div>
            </div>
            <div className="gamereview_modal_body">
              <div className="gamereview_scores">
                {selectedReview.gameReviewData?.graphics && (
                  <div className="gamereview_score_item">
                    <span className="gamereview_score_label">Gráficos</span>
                    <div className="gamereview_score_bar">
                      <div style={{ width: `${selectedReview.gameReviewData.graphics * 10}%` }}></div>
                    </div>
                    <span className="gamereview_score_value">{selectedReview.gameReviewData.graphics}</span>
                  </div>
                )}
                {selectedReview.gameReviewData?.gameplay && (
                  <div className="gamereview_score_item">
                    <span className="gamereview_score_label">Gameplay</span>
                    <div className="gamereview_score_bar">
                      <div style={{ width: `${selectedReview.gameReviewData.gameplay * 10}%` }}></div>
                    </div>
                    <span className="gamereview_score_value">{selectedReview.gameReviewData.gameplay}</span>
                  </div>
                )}
                {selectedReview.gameReviewData?.story && (
                  <div className="gamereview_score_item">
                    <span className="gamereview_score_label">História</span>
                    <div className="gamereview_score_bar">
                      <div style={{ width: `${selectedReview.gameReviewData.story * 10}%` }}></div>
                    </div>
                    <span className="gamereview_score_value">{selectedReview.gameReviewData.story}</span>
                  </div>
                )}
              </div>
              <div className="gamereview_review_text">
                <h4>Review</h4>
                <p>{selectedReview.gameReviewData?.review || selectedReview.text}</p>
              </div>
              {selectedReview.gameReviewData?.pros?.length > 0 && (
                <div className="gamereview_pros">
                  <h4>Pontos Positivos</h4>
                  <ul>
                    {selectedReview.gameReviewData.pros.map((pro, i) => (
                      <li key={i}>{pro}</li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedReview.gameReviewData?.cons?.length > 0 && (
                <div className="gamereview_cons">
                  <h4>Pontos Negativos</h4>
                  <ul>
                    {selectedReview.gameReviewData.cons.map((con, i) => (
                      <li key={i}>{con}</li>
                    ))}
                  </ul>
                </div>
              )}
              <Link to={`/profile/${selectedReview.user.username}`} className="gamereview_modal_author">
                <img src={selectedReview.user.picture} alt="" />
                <span>Review por {selectedReview.user.first_name} {selectedReview.user.last_name}</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
